<template>
    <div class="learningpath-banner-info">
        <div class="learningpath-banner-info__row">
            <div class="learningpath-banner-info__item">
                <span class="learningpath-banner-info__label" v-html="$t('BannerCollectionInfo.total_duration')"></span>
                <span class="learningpath-banner-info__data">
                    {{ convertMinutesToHours(duration) }}
                </span>
            </div>

            <div class="learningpath-banner-info__item">
                <span class="learningpath-banner-info__label" v-html="$t('BannerCollectionInfo.course_count')"></span>
                <span class="learningpath-banner-info__data">{{ amountOfCourses }}</span>
            </div>

            <div v-if="levels.length > 0" class="learningpath-banner-info__item">
                <span class="learningpath-banner-info__label" v-html="$t('General.level')"></span>
                <span class="learningpath-banner-info__data -clickable" v-for="(level, index) in levelsObjectArray"
                      :key="index"
                      @click="goToListWithPreFilteredOption(level, 'difficulty_levels')"
                >
                    {{ $t(`Select.difficulty_level_note_${level.id}`) }}
                </span>
            </div>

            <div class="learningpath-banner-info__item" v-if="showRecommendedPace">
                <span class="learningpath-banner-info__label" v-html="$t('LearningPath.recommended_pace')"></span>
                <span class="learningpath-banner-info__data">
                    {{ recommendedPace }}
                </span>
            </div>

            <div v-if="categories.length > 0" class="learningpath-banner-info__item">
                <span class="learningpath-banner-info__label" v-html="$t('General.categories')"></span>
                <div
                    class="learningpath-banner-info__category-with-ancestors"
                    :key="index"
                    v-for="(categoryWithAncestors, index) in categories"
                >
                    <span class="learningpath-banner-info__data -clickable -link-color"
                          v-for="(category, catIndex) in categoryWithAncestors"
                          :key="catIndex"
                          @click="goToListWithPreFilteredOption(category, 'categories')"
                    >
                        <span>{{ category.label }}</span>
                        <template v-if="catIndex < categoryWithAncestors.length - 1"> > </template>
                    </span>
                    <template v-if="index < categories.length - 1">,</template>
                </div>
            </div>
        </div>

        <div class="learningpath-banner-info__row">
            <div class="learningpath-banner-info__item" v-if="lastUpdatedDate">
                <span class="learningpath-banner-info__label" v-html="$t('CourseDates.last_updated')"></span>
                <span class="learningpath-banner-info__data">{{ formattedDateForCountry(lastUpdatedDate) }}</span>
            </div>
            <div class="learningpath-banner-info__item" v-if="publishDate">
                <span class="learningpath-banner-info__label" v-html="$t('CourseDates.publish_date')"></span>
                <span class="learningpath-banner-info__data -regular">{{ formattedDateForCountry(publishDate) }}</span>
            </div>
            <div class="learningpath-banner-info__item" v-if="expirationDate">
                <span class="learningpath-banner-info__label" v-html="$t('CourseDates.expiration_date')"></span>
                <span class="learningpath-banner-info__data -regular">{{ formattedDateForCountry(expirationDate) }}</span>
            </div>
        </div>

        <div class="learningpath-banner-info__item -authorizations" v-if="authorizations.length > 0">
            <span class="learningpath-banner-info__label" v-html="$t('BannerCollectionInfo.authorization_hours')"></span>
                <TotemTag
                    v-for="(authorization, index) in authorizations"
                    :key="index"
                    :label="authorization.authorizationName"
                    :name="authorization.authorizationPoints"
                    :clear="false"
                />
        </div>
    </div>
</template>

<script>
    import goToListWithPreFilteredOption from 'components/shared/mixins/goToListWithPreFilteredOption'
    import { formattedDateForCountry } from 'components/shared/mixins/dateHelper'
    import convertMinutesToHours from 'components/shared/mixins/convertMinutesToHours'

    export default {
        name: 'totem-learningpath-info',
        mixins: [ goToListWithPreFilteredOption, convertMinutesToHours ],
        props: {
            categories: {
                type: Array,
                default: () => []
            },
            levels: {
                type: Array,
                default: () => []
            },
            publishDate: String,
            expirationDate: String,
            lastUpdatedDate: String,
            duration: Number,
            amountOfCourses: Number,
            authorizationPoints: Array,
            recommendedPace: String
        },
        data () {
            return {
                showCategories: false
            }
        },
        computed: {
            levelsObjectArray () {
                return this.levels.map(el => ({
                    id: el,
                    label: el
                }))
            },
            authorizations () {
                return this.authorizationPoints.filter(authorization => authorization.authorizationPoints > 0)
            },
            showRecommendedPace () {
                return this.recommendedPace && this.recommendedPace.trim().length > 0
            }
        },
        methods: {
            formattedDateForCountry
        }
    }
</script>

<style lang="scss" scoped>
    .learningpath-banner-info {
        font-size: 1.6rem;
        display: flex;
        position: relative;
        flex-direction: column;
        margin: 30px 0;
        padding: 10px;
        background: $concrete;
        border-radius: 4px;
        width: 100%;

        &__row {
            display: flex;
            gap: 12px;
            margin: 0 0 15px;

            &:last-of-type {
                margin: 0;
            }
        }

        &__item {
            position: relative;
            display: flex;
            align-items: center;
            flex-wrap: wrap;

            &.-authorizations {
                margin: -8px 0 -5px;
            }
        }

        &__label {
            font-family: PlusJakartaSans-SemiBold, sans-serif;
            padding: 0 5px 0 0;
        }

        &__data {
            font-family: PlusJakartaSans-Regular, sans-serif;
            position: relative;

            &.-clickable {
                cursor: pointer;
            }

            &.-link-color {
                color: $lavender;
                text-decoration: underline;
            }

            &.-regular {
                font-family: PlusJakartaSans-Regular, sans-serif;
            }
        }

        &__category-with-ancestors {
            padding-right: 5px;
        }

        &__category-with-ancestors:last-child {
            padding-right: 0;
        }
    }
</style>
